import React, { useEffect, useRef } from "react";
import { Bold, Italic, Underline, Highlighter, List, ListOrdered, Link2, Eraser } from "lucide-react";

const ToolbarButton = ({ title, onClick, children, testid }) => (
  <button
    type="button"
    title={title}
    data-testid={testid}
    onMouseDown={(e) => { e.preventDefault(); onClick(); }}
    className="h-8 w-8 flex items-center justify-center rounded text-slate-600 hover:bg-slate-200 hover:text-slate-900 transition-colors"
  >
    {children}
  </button>
);

// Lightweight Gmail-style rich text editor (contentEditable + execCommand).
export const RichTextEditor = ({ value, onChange, testid, placeholder = "Write your message…" }) => {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (el && value !== undefined && value !== el.innerHTML) {
      el.innerHTML = value || "";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const exec = (command, arg = null) => {
    document.execCommand(command, false, arg);
    ref.current?.focus();
    onChange(ref.current?.innerHTML || "");
  };

  const addLink = () => {
    const url = window.prompt("Enter the link URL (include https://)");
    if (url) exec("createLink", url);
  };

  return (
    <div className="rounded-md border border-slate-300 focus-within:ring-2 focus-within:ring-blue-500/40 focus-within:border-blue-500 overflow-hidden bg-white">
      <div className="flex items-center gap-0.5 border-b border-slate-200 bg-slate-50 px-1.5 py-1 flex-wrap">
        <ToolbarButton title="Bold" testid="rte-bold" onClick={() => exec("bold")}><Bold className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton title="Italic" testid="rte-italic" onClick={() => exec("italic")}><Italic className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton title="Underline" testid="rte-underline" onClick={() => exec("underline")}><Underline className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton title="Highlight" testid="rte-highlight" onClick={() => exec("hiliteColor", "#fde68a")}><Highlighter className="h-4 w-4" /></ToolbarButton>
        <span className="w-px h-5 bg-slate-200 mx-1" />
        <ToolbarButton title="Bulleted list" testid="rte-ul" onClick={() => exec("insertUnorderedList")}><List className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton title="Numbered list" testid="rte-ol" onClick={() => exec("insertOrderedList")}><ListOrdered className="h-4 w-4" /></ToolbarButton>
        <ToolbarButton title="Insert link" testid="rte-link" onClick={addLink}><Link2 className="h-4 w-4" /></ToolbarButton>
        <span className="w-px h-5 bg-slate-200 mx-1" />
        <ToolbarButton title="Clear formatting" testid="rte-clear" onClick={() => exec("removeFormat")}><Eraser className="h-4 w-4" /></ToolbarButton>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        data-testid={testid}
        onInput={(e) => onChange(e.currentTarget.innerHTML)}
        data-placeholder={placeholder}
        className="rte-content min-h-[220px] max-h-[40vh] overflow-y-auto px-3 py-2.5 text-sm text-slate-800 leading-relaxed focus:outline-none"
      />
    </div>
  );
};

export default RichTextEditor;
